<div class="electronic-journal">
  <!-- Header -->
  <div class="row mb-4 align-items-center">
    <div class="col-md-6">
      <h4>Electronic Journal</h4>
      <p class="text-muted">Immutable audit log of all sales transactions</p>
    </div>
    <div class="col-md-6 text-end">
      <span class="text-muted me-3" *ngIf="!isLoading">
        {{ totalEntries }} entr{{ totalEntries !== 1 ? 'ies' : 'y' }} found
      </span>
      <button class="btn btn-outline-secondary btn-sm" (click)="loadEntries()">
        <nb-icon icon="refresh-outline"></nb-icon>
        Refresh
      </button>
    </div>
  </div>

  <!-- Filters -->
  <nb-card class="mb-4">
    <nb-card-body>
      <div class="row align-items-end g-3">
        <div class="col-md-2">
          <label class="form-label">Start Date</label>
          <input type="date" class="form-control" [(ngModel)]="startDate">
        </div>
        <div class="col-md-2">
          <label class="form-label">End Date</label>
          <input type="date" class="form-control" [(ngModel)]="endDate">
        </div>
        <div class="col-md-3">
          <label class="form-label">Payment Method</label>
          <select class="form-select" [(ngModel)]="selectedPaymentMethod">
            <option *ngFor="let pm of paymentMethods" [value]="pm.value">{{ pm.label }}</option>
          </select>
        </div>
        <div class="col-md-2 d-flex align-items-end">
          <div class="form-check">
            <input
              class="form-check-input"
              type="checkbox"
              id="testOnly"
              [(ngModel)]="isTestOnly">
            <label class="form-check-label" for="testOnly">
              Test receipts only
            </label>
          </div>
        </div>
        <div class="col-md-3 d-flex gap-2 align-items-end">
          <button class="btn btn-primary w-100" (click)="applyFilters()">
            Apply Filters
          </button>
          <button class="btn btn-outline-secondary w-100" (click)="resetFilters()">
            Reset
          </button>
        </div>
      </div>
    </nb-card-body>
  </nb-card>

  <!-- Table -->
  <nb-card>
    <nb-card-body class="p-0">
      <div class="table-responsive">
        <table class="table table-hover mb-0">
          <thead class="table-light">
            <tr>
              <th>Entry #</th>
              <th>Timestamp</th>
              <th>Sale End Time</th>
              <th>Receipt #</th>
              <th>Manager</th>
              <th>Terminal</th>
              <th>POS Serial</th>
              <th>Payment Method</th>
              <th class="text-end">Amount</th>
              <th>Flags</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let entry of entries; trackBy: trackByEntryId" class="cursor-pointer">
              <td class="font-monospace small">{{ entry.entryNumber }}</td>
              <td class="text-nowrap">{{ entry.timestamp | date:'dd/MM/yyyy HH:mm' }}</td>
              <td class="text-nowrap">{{ entry.saleEndTime ? (entry.saleEndTime | date:'dd/MM/yyyy HH:mm') : '—' }}</td>
              <td class="font-monospace small">{{ entry.receiptNumber }}</td>
              <td>{{ entry.managerName || '—' }}</td>
              <td>{{ entry.terminalName || (entry.terminalId ? entry.terminalId.substring(0, 8) + '...' : '—') }}</td>
              <td>{{ entry.posSerialNumber || '—' }}</td>
              <td>
                <span class="badge bg-light text-dark border">
                  {{ formatPaymentMethod(entry.paymentMethod) }}
                </span>
              </td>
              <td class="text-end text-nowrap"
                [class.text-danger]="entry.totalAmount < 0"
                [class.fw-bold]="entry.isVoided">
                {{ entry.isVoided ? '(' + formatCurrency(Math.abs(entry.totalAmount), entry.currency) + ')' : formatCurrency(entry.totalAmount, entry.currency) }}
              </td>
              <td>
                <span *ngIf="entry.isTestReceipt" class="badge bg-warning text-dark me-1">TEST</span>
                <span *ngIf="entry.isVoided" class="badge bg-danger me-1">VOID</span>
                <span *ngIf="entry.originalEntryId" class="badge bg-secondary me-1">REFUND</span>
              </td>
              <td>
                <button
                  class="btn btn-sm btn-outline-primary"
                  (click)="viewEntryDetails(entry)"
                  nbTooltip="View entry detail">
                  <nb-icon icon="eye-outline"></nb-icon>
                  View
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Loading State -->
      <div *ngIf="isLoading" class="text-center p-4">
        <nb-spinner size="large"></nb-spinner>
        <p class="mt-2 text-muted">Loading journal entries...</p>
      </div>

      <!-- Error State -->
      <div *ngIf="!isLoading && errorMessage" class="text-center p-4">
        <nb-icon icon="alert-circle-outline" class="text-danger" style="font-size: 2rem;"></nb-icon>
        <p class="mt-2 text-danger">{{ errorMessage }}</p>
        <button class="btn btn-outline-secondary btn-sm" (click)="loadEntries()">Try Again</button>
      </div>

      <!-- Empty State -->
      <div *ngIf="!isLoading && !errorMessage && entries.length === 0" class="text-center p-4">
        <nb-icon icon="file-text-outline" style="font-size: 2.5rem;" class="text-muted mb-2"></nb-icon>
        <h5>No journal entries found</h5>
        <p class="text-muted">No data for this period or filter combination.</p>
      </div>

      <!-- Pagination -->
      <div
        *ngIf="!isLoading && totalPages > 1"
        class="d-flex justify-content-between align-items-center p-3 border-top">
        <div>
          <span class="text-muted">
            Showing {{ (currentPage - 1) * pageSize + 1 }} to
            {{ Math.min(currentPage * pageSize, totalEntries) }} of {{ totalEntries }} entries
          </span>
        </div>
        <div>
          <button
            class="btn btn-outline-secondary me-2"
            [disabled]="currentPage === 1"
            (click)="onPageChange(currentPage - 1)">
            Previous
          </button>
          <span class="me-2">Page {{ currentPage }} of {{ totalPages }}</span>
          <button
            class="btn btn-outline-secondary"
            [disabled]="currentPage === totalPages"
            (click)="onPageChange(currentPage + 1)">
            Next
          </button>
        </div>
      </div>
    </nb-card-body>
  </nb-card>
</div>

<!-- Entry Detail Modal -->
<nb-card *ngIf="showEntryDetails" class="modal-overlay">
  <nb-card-header class="d-flex justify-content-between align-items-center">
    <div class="d-flex align-items-center gap-3">
      <h5 class="mb-0">Journal Entry Detail</h5>
      <span *ngIf="selectedEntry?.isTestReceipt" class="badge bg-warning text-dark">TEST</span>
      <span *ngIf="selectedEntry?.isVoided" class="badge bg-danger">VOID</span>
      <span class="text-muted small font-monospace">
        #{{ selectedEntry?.entryNumber }} — {{ selectedEntry?.timestamp | date:'dd/MM/yyyy HH:mm:ss' }}
      </span>
    </div>
    <button class="btn btn-ghost" (click)="closeEntryDetails()">
      <nb-icon icon="close-outline"></nb-icon>
    </button>
  </nb-card-header>

  <nb-card-body *ngIf="selectedEntry" class="modal-body">
    <!-- Meta info -->
    <div class="row mb-4">
      <div class="col-md-6">
        <h6 class="section-label">Transaction Information</h6>
        <p><strong>Receipt #:</strong> <span class="ms-2 font-monospace">{{ selectedEntry.receiptNumber }}</span></p>
        <p><strong>Payment Method:</strong> <span class="ms-2">{{ formatPaymentMethod(selectedEntry.paymentMethod) }}</span></p>
        <p><strong>Manager:</strong> <span class="ms-2">{{ selectedEntry.managerName || selectedEntry.managerId || '—' }}</span></p>
        <p><strong>Terminal:</strong> <span class="ms-2">{{ selectedEntry.terminalName || selectedEntry.terminalId || '—' }}</span></p>
        <p *ngIf="selectedEntry.originalEntryId">
          <strong>Original Entry ID:</strong>
          <span class="ms-2 font-monospace small">{{ selectedEntry.originalEntryId }}</span>
        </p>
      </div>
      <div class="col-md-6">
        <h6 class="section-label">Totals</h6>
        <p><strong>Net (excl. tax):</strong> <span class="ms-2">{{ formatCurrency(selectedEntry.subtotalExcludingTax, selectedEntry.currency) }}</span></p>
        <p><strong>Tax:</strong> <span class="ms-2">{{ formatCurrency(selectedEntry.totalTaxAmount, selectedEntry.currency) }}</span></p>
        <p class="fw-bold"><strong>Total:</strong>
          <span class="ms-2" [class.text-danger]="selectedEntry.totalAmount < 0">
            {{ formatCurrency(selectedEntry.totalAmount, selectedEntry.currency) }}
          </span>
        </p>
      </div>
    </div>

    <!-- Items Table -->
    <h6 class="section-label">
      Line Items
      <span class="badge bg-secondary ms-2">{{ selectedEntry.items?.length || 0 }}</span>
    </h6>

    <div *ngIf="selectedEntry.items && selectedEntry.items.length > 0; else noItems" class="table-responsive">
      <table class="table table-sm table-bordered">
        <thead class="table-light">
          <tr>
            <th>Product</th>
            <th>SKU</th>
            <th class="text-center">Qty</th>
            <th class="text-end">Unit Price (incl. tax)</th>
            <th class="text-end">Tax Rate</th>
            <th class="text-end">Tax Amount</th>
            <th class="text-end">Total</th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let item of selectedEntry.items" [class.table-danger]="item.isRefund">
            <td>{{ item.productName || '—' }}</td>
            <td class="font-monospace small text-muted">{{ item.sku || '—' }}</td>
            <td class="text-center">{{ item.quantity }}</td>
            <td class="text-end">{{ formatCurrency(item.unitPrice, selectedEntry.currency) }}</td>
            <td class="text-end">{{ item.taxRate | number:'1.0-1' }}%</td>
            <td class="text-end">{{ formatCurrency(item.taxAmount, selectedEntry.currency) }}</td>
            <td class="text-end fw-bold">{{ formatCurrency(item.totalPrice, selectedEntry.currency) }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <ng-template #noItems>
      <p class="text-muted fst-italic">No line items recorded for this entry.</p>
    </ng-template>
  </nb-card-body>
</nb-card>
